import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Confirm, Notify, Report } from 'notiflix';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import styled from 'styled-components';
import SignaturePadModal from '../../../components/modal/SignaturePadModal';
import { isAdmin } from '../../../config/roles';
import {
    cancelPrizePayout,
    getPendingWinners,
    getPrizePayouts,
    signPrizePayout,
    triggerPrizePayoutPdfDownload,
    triggerPrizePayoutProofDownload,
} from '../../../data/api/prizePayouts/PrizePayoutRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';
import EditPayoutModal from './EditPayoutModal';
import InitiatePayoutModal from './InitiatePayoutModal';
import PayPayoutModal from './PayPayoutModal';

const fmtCOP = (cents) => {
    const value = Number(cents ?? 0) / 100;
    return value.toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0,
    });
};

const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleString('es-CO', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

const STATUS_META = {
    PENDING: { label: 'Pendiente firma', color: '#8a5a00', bg: 'rgba(214, 170, 58, 0.16)', border: 'rgba(214, 170, 58, 0.32)' },
    SIGNED: { label: 'Firmado', color: '#10285f', bg: 'rgba(16, 40, 95, 0.10)', border: 'rgba(16, 40, 95, 0.30)' },
    PAID: { label: 'Entregado', color: '#1e6a36', bg: 'rgba(46, 125, 50, 0.14)', border: 'rgba(46, 125, 50, 0.32)' },
    CANCELLED: { label: 'Cancelado', color: '#b71c1c', bg: 'rgba(211, 47, 47, 0.10)', border: 'rgba(211, 47, 47, 0.30)' },
    EXPIRED: { label: 'Expirado', color: '#555', bg: 'rgba(0, 0, 0, 0.06)', border: 'rgba(0, 0, 0, 0.16)' },
};

const PrizePayoutsPage = () => {
    const { auth, token } = useIndex();
    const admin = isAdmin(auth);

    /* ============= Pending winners (admin) ============= */
    const [pendingWinners, setPendingWinners] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);

    /* ============= Prize payouts list ============= */
    const [payouts, setPayouts] = useState([]);
    const [meta, setMeta] = useState(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [loadingPayouts, setLoadingPayouts] = useState(false);

    /* ============= Filters ============= */
    const [filterSearch, setFilterSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterMethod, setFilterMethod] = useState('');
    const [filterGameCode, setFilterGameCode] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [filterMinCop, setFilterMinCop] = useState('');
    const [filterMaxCop, setFilterMaxCop] = useState('');

    /* ============= Modals ============= */
    const [initiateOpen, setInitiateOpen] = useState(false);
    const [winnerForInitiate, setWinnerForInitiate] = useState(null);

    const [signOpen, setSignOpen] = useState(false);
    const [payoutForSign, setPayoutForSign] = useState(null);

    const [editOpen, setEditOpen] = useState(false);
    const [payoutForEdit, setPayoutForEdit] = useState(null);

    const [payOpen, setPayOpen] = useState(false);
    const [payoutForPay, setPayoutForPay] = useState(null);

    const loadPendingWinners = useCallback(async () => {
        // Sin token (caso de logout en curso) o sin permisos de admin
        // no tiene sentido pegarle al endpoint — pegaría con
        // "Bearer null" y devolvería 401, gatillando el flujo de
        // "sesión expirada" del interceptor encima del logout normal.
        if (!token || !admin) return;
        setLoadingPending(true);
        try {
            const { data } = await getPendingWinners(token);
            if (data?.success) setPendingWinners(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        } finally {
            setLoadingPending(false);
        }
    }, [admin, token]);

    const buildFilterParams = useCallback((p = page) => {
        const params = { per_page: perPage, page: p };
        if (filterSearch) params.search = filterSearch.trim();
        if (filterStatus) params.status = filterStatus;
        if (filterMethod) params.payment_method = filterMethod;
        if (filterGameCode) params.game_code = filterGameCode.trim();
        if (filterDateFrom) params.date_from = filterDateFrom;
        if (filterDateTo) params.date_to = filterDateTo;
        // El backend filtra `gross_amount` en CENTAVOS — el usuario
        // tipea en COP, convertimos al enviar.
        if (filterMinCop) params.min_amount = Number(filterMinCop) * 100;
        if (filterMaxCop) params.max_amount = Number(filterMaxCop) * 100;
        return params;
    }, [page, perPage, filterSearch, filterStatus, filterMethod, filterGameCode,
        filterDateFrom, filterDateTo, filterMinCop, filterMaxCop]);

    const loadPayouts = useCallback(async (p = page) => {
        // Mismo motivo que arriba: sin token, no llamamos.
        if (!token) return;
        setLoadingPayouts(true);
        try {
            const { data } = await getPrizePayouts(token, buildFilterParams(p));
            if (data?.success) {
                setPayouts(data?.data ?? []);
                setMeta(data?.meta ?? null);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoadingPayouts(false);
        }
    }, [page, token, buildFilterParams]);

    useEffect(() => {
        loadPendingWinners();
    }, [loadPendingWinners]);

    // 1. Cambio de página → cargar esa página con los filtros vigentes.
    //    Omitimos loadPayouts del array para evitar dobles disparos
    //    cuando los filtros (que están en sus deps) cambian a la par.
    useEffect(() => {
        loadPayouts(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // 2. Cambio de filtros "instantáneos" (selects, date pickers,
    //    per_page) → vamos a página 1 (lo que dispara el efecto 1)
    //    o si ya estamos en 1, recargamos directo.
    useEffect(() => {
        if (page !== 1) setPage(1);
        else loadPayouts(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus, filterMethod, filterDateFrom, filterDateTo, perPage]);

    // 3. Cambio de filtros "tipeables" (search, game_code, amounts) →
    //    debounce de 600ms para no machacar la API en cada tecla.
    //    `cancel()` en el mount evita que el debounce dispare al inicio.
    const [, cancelTypedFilters] = useDebounce(() => {
        if (page !== 1) setPage(1);
        else loadPayouts(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 600, [filterSearch, filterGameCode, filterMinCop, filterMaxCop]);
    useEffect(() => {
        cancelTypedFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ============= Actions ============= */

    const openInitiate = (winner) => {
        setWinnerForInitiate(winner);
        setInitiateOpen(true);
    };

    const openSign = (payout) => {
        setPayoutForSign(payout);
        setSignOpen(true);
    };

    const openEdit = (payout) => {
        setPayoutForEdit(payout);
        setEditOpen(true);
    };

    const resetFilters = () => {
        setFilterSearch('');
        setFilterStatus('');
        setFilterMethod('');
        setFilterGameCode('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterMinCop('');
        setFilterMaxCop('');
        setPage(1);
    };

    const hasActiveFilters = useMemo(() => (
        !!filterSearch || !!filterStatus || !!filterMethod || !!filterGameCode
        || !!filterDateFrom || !!filterDateTo || !!filterMinCop || !!filterMaxCop
    ), [filterSearch, filterStatus, filterMethod, filterGameCode,
        filterDateFrom, filterDateTo, filterMinCop, filterMaxCop]);

    const handleDownloadPdf = async (payout) => {
        try {
            const safeName = (payout.winner?.user_name ?? 'ganador')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .slice(0, 40);
            await triggerPrizePayoutPdfDownload(
                token,
                payout.uuid,
                `comprobante-premio-${safeName}-${payout.uuid.slice(0, 8)}.pdf`,
            );
        } catch (error) {
            validErrors(error);
        }
    };

    const handleSignSave = async (dataUrl) => {
        if (!payoutForSign) return;
        try {
            const { data } = await signPrizePayout(token, payoutForSign.uuid, dataUrl);
            if (data?.success) {
                Report.success('Firma registrada', data?.message ?? 'Compromiso firmado.', 'Aceptar');
                setSignOpen(false);
                setPayoutForSign(null);
                loadPayouts(page);
            }
        } catch (error) {
            validErrors(error);
        }
    };

    const openPay = (payout) => {
        setPayoutForPay(payout);
        setPayOpen(true);
    };

    const handleDownloadProof = async (payout) => {
        try {
            const safeName = (payout.winner?.user_name ?? 'ganador')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .slice(0, 40);
            await triggerPrizePayoutProofDownload(
                token,
                payout.uuid,
                `soporte-pago-${safeName}-${payout.uuid.slice(0, 8)}`,
            );
        } catch (error) {
            validErrors(error);
        }
    };

    const handleCancel = (payout) => {
        Confirm.prompt(
            'Cancelar pago',
            'Indica el motivo de cancelación:',
            '',
            'Cancelar pago',
            'Volver',
            async (reason) => {
                if (!reason || !reason.trim()) {
                    Notify.failure('El motivo es obligatorio.');
                    return;
                }
                try {
                    const { data } = await cancelPrizePayout(token, payout.uuid, reason.trim());
                    if (data?.success) {
                        Report.success('Pago cancelado', data?.message ?? 'OK', 'Aceptar');
                        loadPayouts(page);
                        loadPendingWinners();
                    }
                } catch (error) {
                    validErrors(error);
                }
            },
            () => { },
        );
    };

    const isOwner = (payout) =>
        payout?.winner && Number(payout.winner.user_id) === Number(auth?.id);

    return (
        <PageContainer maxWidth={false}>
            {/* ===================================================
                SECCIÓN 1 — Ganadores con premio en dinero pendientes
                de iniciar pago (solo admin).
                =================================================== */}
            {/* Solo se muestra cuando efectivamente hay candidatos.
                Con el flujo de auto-creación la lista debería estar
                vacía siempre — esta sección queda como fallback para
                casos donde la creación automática no se ejecutó (p. ej.
                premios anteriores al fix, o errores transitorios). */}
            {admin && pendingWinners.length > 0 && (
                <Section>
                    <SectionHeader>
                        <Box>
                            <Typography className="section-title">Premios pendientes de iniciar</Typography>
                            <Typography className="section-subtitle">
                                Ganadores con premio en dinero sin orden de pago. Crear manualmente.
                            </Typography>
                        </Box>
                        <Tooltip title="Recargar">
                            <IconButton size="small" onClick={loadPendingWinners}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </SectionHeader>

                    {/* Sabemos que hay al menos uno por la guard de
                        arriba; renderizamos directo. Si está cargando
                        igual mostramos los anteriores para no flickear. */}
                    <Stack spacing={1}>
                        {pendingWinners.map((w) => (
                            <PendingRow key={w.uuid}>
                                <Box className="info">
                                    <Typography className="name">
                                        {w.user_name} <span className="doc">· Doc: {w.document ?? '-'}</span>
                                    </Typography>
                                    <Box className="meta">
                                        <Chip size="small" label={`${w.result_hits} aciertos`} />
                                        <Chip size="small" variant="outlined" label={`Juego: ${w.game_code}`} />
                                        <Chip size="small" color="warning" label={prizesPreview(w.prizes)} />
                                    </Box>
                                </Box>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<RedeemOutlinedIcon />}
                                    onClick={() => openInitiate(w)}
                                    sx={{
                                        borderRadius: '10px',
                                        fontWeight: 900,
                                        textTransform: 'uppercase',
                                        background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    }}
                                >
                                    Iniciar pago
                                </Button>
                            </PendingRow>
                        ))}
                    </Stack>
                </Section>
            )}

            {/* ===================================================
                SECCIÓN 2 — Listado de pagos (todos los estados).
                =================================================== */}
            <Section>
                <SectionHeader>
                    <Box>
                        <Typography className="section-title">Pagos de premios</Typography>
                        <Typography className="section-subtitle">
                            {admin
                                ? 'Todos los pagos del sistema. Acciones según estado.'
                                : 'Tus pagos pendientes de firma o ya entregados.'}
                        </Typography>
                    </Box>
                    <Tooltip title="Recargar">
                        <IconButton size="small" onClick={() => loadPayouts(page)}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </SectionHeader>

                {/* ============= FILTROS ============= */}
                <FiltersCard>
                    <Grid container spacing={1.2}>
                        {admin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Buscar (jugador, documento, correo o código)"
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        )}

                        <Grid item xs={6} md={3} lg={2}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    label="Estado"
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="PENDING">Pendiente firma</MenuItem>
                                    <MenuItem value="SIGNED">Firmado</MenuItem>
                                    <MenuItem value="PAID">Entregado</MenuItem>
                                    <MenuItem value="CANCELLED">Cancelado</MenuItem>
                                    <MenuItem value="EXPIRED">Expirado</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {admin && (
                            <Grid item xs={6} md={3} lg={2}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Método</InputLabel>
                                    <Select
                                        label="Método"
                                        value={filterMethod}
                                        onChange={(e) => setFilterMethod(e.target.value)}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        <MenuItem value="EFECTIVO">Efectivo</MenuItem>
                                        <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
                                        <MenuItem value="NEQUI">Nequi</MenuItem>
                                        <MenuItem value="DAVIPLATA">Daviplata</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {admin && (
                            <Grid item xs={12} md={6} lg={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Código del juego"
                                    value={filterGameCode}
                                    onChange={(e) => setFilterGameCode(e.target.value)}
                                    placeholder="Ej: TEST_AYE2..."
                                />
                            </Grid>
                        )}

                        <Grid item xs={6} md={3} lg={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Desde"
                                InputLabelProps={{ shrink: true }}
                                value={filterDateFrom}
                                onChange={(e) => setFilterDateFrom(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={6} md={3} lg={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Hasta"
                                InputLabelProps={{ shrink: true }}
                                value={filterDateTo}
                                onChange={(e) => setFilterDateTo(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={6} md={3} lg={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Valor mínimo (COP)"
                                value={filterMinCop}
                                onChange={(e) => setFilterMinCop(e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                inputProps={{ min: 0, step: 1000 }}
                            />
                        </Grid>

                        <Grid item xs={6} md={3} lg={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Valor máximo (COP)"
                                value={filterMaxCop}
                                onChange={(e) => setFilterMaxCop(e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                inputProps={{ min: 0, step: 1000 }}
                            />
                        </Grid>

                        <Grid item xs={6} md={3} lg={2}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Por página</InputLabel>
                                <Select
                                    label="Por página"
                                    value={perPage}
                                    onChange={(e) => setPerPage(Number(e.target.value))}
                                >
                                    <MenuItem value={10}>10</MenuItem>
                                    <MenuItem value={25}>25</MenuItem>
                                    <MenuItem value={50}>50</MenuItem>
                                    <MenuItem value={100}>100</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {hasActiveFilters && (
                            <Grid item xs={12} md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={<RestartAltIcon />}
                                    onClick={resetFilters}
                                    sx={{ textTransform: 'none', fontWeight: 700 }}
                                >
                                    Limpiar filtros
                                </Button>
                            </Grid>
                        )}
                    </Grid>

                    {meta && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.2 }}>
                            <Chip
                                size="small"
                                label={`Total: ${meta?.total ?? 0}`}
                                sx={{ fontWeight: 700 }}
                            />
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Página ${meta?.current_page ?? page} de ${meta?.last_page ?? 1}`}
                            />
                        </Box>
                    )}
                </FiltersCard>

                {loadingPayouts ? (
                    <Stack spacing={1}>
                        {[1, 2, 3].map((k) => (
                            <Skeleton key={k} variant="rectangular" height={110} sx={{ borderRadius: 2 }} />
                        ))}
                    </Stack>
                ) : payouts.length === 0 ? (
                    <EmptyBox>Aún no hay pagos registrados.</EmptyBox>
                ) : (
                    <Stack spacing={1}>
                        {payouts.map((p) => {
                            const meta = STATUS_META[p.status] ?? STATUS_META.PENDING;
                            const owner = isOwner(p);
                            return (
                                <PayoutCard key={p.uuid}>
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        justifyContent="space-between"
                                        alignItems={{ xs: 'flex-start', md: 'center' }}
                                        spacing={1.4}
                                    >
                                        <Box>
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Chip
                                                    size="small"
                                                    label={meta.label}
                                                    sx={{
                                                        fontWeight: 900,
                                                        color: meta.color,
                                                        background: meta.bg,
                                                        border: `1px solid ${meta.border}`,
                                                    }}
                                                />
                                                <Chip size="small" variant="outlined" label={`Método: ${p.payment_method}`} />
                                                <Chip size="small" variant="outlined" label={`Plazo: ${p.deadline_at ?? '-'}`} />
                                            </Stack>
                                            <Typography sx={{ fontWeight: 900, color: '#10285f', mt: 0.5 }}>
                                                {p.winner?.user_name ?? '-'}{' '}
                                                <span style={{ color: 'rgba(15,34,77,0.6)', fontWeight: 600, fontSize: '.85em' }}>
                                                    · Doc {p.winner?.document ?? '-'}
                                                </span>
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Creado {fmtDate(p.created_at)}
                                                {p.signed_at && ` · Firmado ${fmtDate(p.signed_at)}`}
                                                {p.paid_at && ` · Pagado ${fmtDate(p.paid_at)}`}
                                                {p.cancelled_at && ` · Cancelado ${fmtDate(p.cancelled_at)}`}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography sx={{ fontWeight: 900, color: '#10285f' }}>
                                                Bruto: {fmtCOP(p.gross_amount)}
                                            </Typography>
                                            {p.withholding_applies && (
                                                <Typography variant="caption" color="error">
                                                    Retefuente: − {fmtCOP(p.withholding_amount)}
                                                </Typography>
                                            )}
                                            <Typography sx={{ fontWeight: 900, color: '#1e6a36' }}>
                                                Neto: {fmtCOP(p.net_amount)}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    {/* Acciones contextuales */}
                                    <ActionsBar>
                                        {/* Comprobante PDF disponible cuando hay firma o
                                            ya está pagado. Visible para admin y para el
                                            ganador dueño. */}
                                        {(p.status === 'SIGNED' || p.status === 'PAID') && (admin || owner) && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<DownloadOutlinedIcon />}
                                                onClick={() => handleDownloadPdf(p)}
                                                sx={{
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    color: '#10285f',
                                                    borderColor: 'rgba(15, 34, 77, 0.32)',
                                                    '&:hover': {
                                                        borderColor: '#10285f',
                                                        background: 'rgba(15, 34, 77, 0.04)',
                                                    },
                                                }}
                                            >
                                                Descargar comprobante
                                            </Button>
                                        )}

                                        {p.status === 'PENDING' && owner && (
                                            <LoadingButton
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                startIcon={<BorderColorOutlinedIcon />}
                                                onClick={() => openSign(p)}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Firmar compromiso
                                            </LoadingButton>
                                        )}

                                        {(p.status === 'PENDING' || p.status === 'SIGNED') && admin && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<EditOutlinedIcon />}
                                                onClick={() => openEdit(p)}
                                                sx={{
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    color: '#10285f',
                                                    borderColor: 'rgba(15, 34, 77, 0.32)',
                                                    '&:hover': {
                                                        borderColor: '#10285f',
                                                        background: 'rgba(15, 34, 77, 0.04)',
                                                    },
                                                }}
                                            >
                                                Editar
                                            </Button>
                                        )}

                                        {p.status === 'SIGNED' && admin && (
                                            <LoadingButton
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                startIcon={<PaidOutlinedIcon />}
                                                onClick={() => openPay(p)}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Aprobar y marcar como pagado
                                            </LoadingButton>
                                        )}

                                        {/* Soporte bancario adjunto al pago. Visible
                                            tanto para admin como para el ganador dueño
                                            (transparencia: el ganador puede verificar
                                            que el pago tiene comprobante). */}
                                        {p.status === 'PAID' && p.has_payment_proof && (admin || owner) && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<ReceiptLongOutlinedIcon />}
                                                onClick={() => handleDownloadProof(p)}
                                                sx={{
                                                    borderRadius: '10px',
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    color: '#1e6a36',
                                                    borderColor: 'rgba(46, 125, 50, 0.32)',
                                                    '&:hover': {
                                                        borderColor: '#1e6a36',
                                                        background: 'rgba(46, 125, 50, 0.04)',
                                                    },
                                                }}
                                            >
                                                Ver soporte bancario
                                            </Button>
                                        )}

                                        {(p.status === 'PENDING' || p.status === 'SIGNED') && admin && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                startIcon={<BlockOutlinedIcon />}
                                                onClick={() => handleCancel(p)}
                                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                                            >
                                                Rechazar
                                            </Button>
                                        )}

                                        {p.status === 'PAID' && (
                                            <Chip
                                                size="small"
                                                icon={<CheckCircleOutlineIcon fontSize="small" />}
                                                label="Premio entregado"
                                                color="success"
                                                variant="outlined"
                                            />
                                        )}
                                    </ActionsBar>
                                </PayoutCard>
                            );
                        })}
                    </Stack>
                )}

                {meta && meta.last_page > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={meta.last_page}
                            page={page}
                            onChange={(_e, n) => setPage(n)}
                            size="small"
                        />
                    </Box>
                )}
            </Section>

            <InitiatePayoutModal
                open={initiateOpen}
                onClose={() => { setInitiateOpen(false); setWinnerForInitiate(null); }}
                winner={winnerForInitiate}
                onCreated={() => { loadPayouts(page); loadPendingWinners(); }}
            />

            <SignaturePadModal
                open={signOpen}
                onClose={() => { setSignOpen(false); setPayoutForSign(null); }}
                onSave={handleSignSave}
                title="Firma del compromiso de premio"
                description={
                    payoutForSign
                        ? `Confirmás que recibirás ${fmtCOP(payoutForSign.net_amount)} en un plazo máximo hasta ${payoutForSign.deadline_at}, vía ${payoutForSign.payment_method}.`
                        : 'Firma para aceptar el compromiso de recepción del premio.'
                }
            />

            <EditPayoutModal
                open={editOpen}
                onClose={() => { setEditOpen(false); setPayoutForEdit(null); }}
                payout={payoutForEdit}
                onUpdated={() => loadPayouts(page)}
            />

            <PayPayoutModal
                open={payOpen}
                onClose={() => { setPayOpen(false); setPayoutForPay(null); }}
                payout={payoutForPay}
                onPaid={() => { loadPayouts(page); loadPendingWinners(); }}
            />
        </PageContainer>
    );
};

export default PrizePayoutsPage;

/* ============= helpers ============= */

const prizesPreview = (prizesRaw) => {
    if (!prizesRaw) return 'Premio en dinero';
    let prizes;
    try {
        prizes = typeof prizesRaw === 'string' ? JSON.parse(prizesRaw) : prizesRaw;
    } catch { return 'Premio en dinero'; }
    if (!Array.isArray(prizes)) return 'Premio en dinero';
    const moneyPrizes = prizes.filter((p) => (p?.prize ?? '').includes('COP'));
    if (moneyPrizes.length === 0) return 'Premio en dinero';
    return moneyPrizes.map((p) => p.prize).join(' · ');
};

/* ============= styles ============= */

const PageContainer = styled(Container)`
    padding: 1rem !important;
`;

const Header = styled.div`
    margin-bottom: 1rem;

    .title {
        color: #10285f;
        font-size: 1.4rem;
        font-weight: 900;
        line-height: 1.1;
    }

    .subtitle {
        margin-top: 0.2rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.88rem;
        font-weight: 600;
    }
`;

const Section = styled.section`
    margin-bottom: 1.5rem;
    padding: 1rem;
    border-radius: 18px;
    background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.06);
`;

const FiltersCard = styled.div`
    margin-bottom: 0.9rem;
    padding: 0.8rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.04);
    border: 1px dashed rgba(15, 34, 77, 0.16);
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.85rem;

    .section-title {
        color: #10285f;
        font-size: 1rem;
        font-weight: 900;
    }

    .section-subtitle {
        margin-top: 0.1rem;
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.8rem;
        font-weight: 600;
    }
`;

const EmptyBox = styled.div`
    padding: 1.2rem;
    text-align: center;
    color: rgba(15, 34, 77, 0.6);
    font-weight: 700;
    background: rgba(16, 40, 95, 0.035);
    border-radius: 12px;
`;

const PendingRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0.9rem;
    border-radius: 12px;
    background: rgba(214, 170, 58, 0.08);
    border: 1px solid rgba(214, 170, 58, 0.22);
    flex-wrap: wrap;

    .name {
        color: #10285f;
        font-weight: 900;

        .doc {
            font-weight: 600;
            color: rgba(15, 34, 77, 0.6);
            font-size: 0.88em;
        }
    }

    .meta {
        margin-top: 0.35rem;
        display: flex;
        gap: 0.3rem;
        flex-wrap: wrap;
    }
`;

const PayoutCard = styled.div`
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: #fff;
    border: 1px solid rgba(15, 34, 77, 0.08);
    box-shadow: 0 4px 10px rgba(15, 34, 77, 0.04);
`;

const ActionsBar = styled.div`
    margin-top: 0.7rem;
    padding-top: 0.7rem;
    border-top: 1px dashed rgba(15, 34, 77, 0.1);
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
`;
